import * as React from 'react';
import { jsx, css } from '@emotion/react';
import { setTime, wait } from 'Src/me/pages/guest/proposals/new/utils';
import { mediaQueries, autoResponsiveClamp } from 'Src/me/pages/guest/proposals/new/React/viewHelpers';
import { notPcViewStyle } from 'Src/me/pages/guest/proposals/new/React/viewByDevice';
import type { FeaturePropsType, NonInteractiveContinueHandlerType } from '../../../../../chat/views/types';
import { RenderContractorSearchInterfacePropsType } from '../../../../../chat/views/ContractorSearchInterface/index';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { Mainvisual } from './components/Mainvisual';
import { StrongPoint } from './components/StrongPoint';
import { SearchByReview } from './components/SearchByReview';
import { SearchByKeyword } from './components/SearchByKeyword';
import { SearchByBudget } from './components/SearchByBudget';
import { SearchByDeterioration } from './components/SearchByDeterioration';
import { SearchByCompanyInfomation } from './components/SearchByCompanyInfomation';
import { SearchByBenefit } from './components/SearchByBenefit';
import { SearchByWallAndRoofConstructionType } from './components/SearchByWallAndRoofConstructionType';
import { SearchByWallConstructionType } from './components/SearchByWallConstructionType';
import { PageTopButton } from './components/PageTopButton';

const { tablet, mobile } = mediaQueries();

export type ContractorSearchInterfacePropsType = FeaturePropsType<RenderContractorSearchInterfacePropsType, NonInteractiveContinueHandlerType>

export const ContractorSearchInterface = (props: ContractorSearchInterfacePropsType) => {
  const {
    isInstant = false,
    continueHandler,
  } = props;
  const [shouldHiddenAnimation, setShouldHiddenAnimation] = React.useState(false);
  const outerElementRef = React.useRef<HTMLDivElement>(null);

  const onApplyHandler = React.useCallback(() => {
    // 次に進むアニメーションを実行
    setShouldHiddenAnimation(true);

    // 回答内容を渡して次の処理に進む
    continueHandler({
      selfDomReferences: [],
    });
  }, [continueHandler]);

  const propsWithApplyHandler = {
    ...props,
    onApplyHandler,
  };

  React.useEffect(() => {
    (async () => {
      const outerElement = outerElementRef.current;

      if (!shouldHiddenAnimation || !outerElement) return;

      // アニメーション開始
      outerElement.style.top = `-${outerElement.clientHeight}px`;

      // スクロールもしておく
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      // アニメーションが終わったら非表示にしておく
      await wait(5);
      outerElement.style.display = 'none';
    })();
  }, [shouldHiddenAnimation]);

  return (
    <div
      ref={outerElementRef}
      css={css`
        width: 100%;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 10;
        transition: top ${setTime(1)}s;

        ${mobile(() => css`
          width: 100dvw;
        `)}
      `}
    >
      <div
        css={css`
          min-height: 100dvh;
          background-color: #fff;
        `}
      >
        <Header {...props} />
        <Mainvisual {...propsWithApplyHandler} />
        <div
          css={css`
            background-color: #f6f6f6;
          `}
        >
          <div
            css={css`
              max-width: 1000px;
              margin-inline: auto;
              padding: 45px 0 20px;

              ${tablet(({clamp}) => css`
                padding-top: ${clamp('220px', '230px')};
              `)}

              ${mobile(({clamp}) => css`
                padding-top: ${clamp('146px', '220px')};
              `)}
            `}
          >
            <div
              css={css`
                ${notPcViewStyle}

                ${tablet(() => css`
                  padding-bottom: 40px;
                `)}
              `}
            >
              <StrongPoint />
            </div>
            <div
              css={css`
                margin-bottom: 50px;

                ${mobile(({ setAutomaticDesignBasedSize }) => css`
                  margin-bottom: ${setAutomaticDesignBasedSize(50, 50)};
                `)}
              `}
            >
              <SearchByReview {...propsWithApplyHandler} />
            </div>
            <div
              css={css`
                margin-bottom: 70px;

                ${mobile(({ setAutomaticDesignBasedSize }) => css`
                  margin-bottom: ${setAutomaticDesignBasedSize(70, 70)};
                `)}
              `}
            >
              <SearchByKeyword {...propsWithApplyHandler} />
            </div>
            <div
              css={css`
                margin-bottom: 70px;
                padding: 65px 80px 40px;
                background-color: #fff;
                border-radius: 20px;

                ${tablet(({ setAutomaticDesignBasedSize }) => css`
                  border-radius: 0;
                  padding: ${setAutomaticDesignBasedSize(65, 45)} 0 ${setAutomaticDesignBasedSize(40, 40)};
                `)}

                ${mobile(({ setAutomaticDesignBasedSize }) => css`
                  padding: ${setAutomaticDesignBasedSize(65, 45)} 0 ${setAutomaticDesignBasedSize(40, 40)};
                `)}
              `}
            >
              <div
                css={css`
                  margin-bottom: 80px;

                  ${tablet(({ setAutomaticDesignBasedSize }) => css`
                    margin-bottom: ${setAutomaticDesignBasedSize(80, 40)};
                  `)}

                  ${mobile(({ setAutomaticDesignBasedSize }) => css`
                    margin-bottom: ${setAutomaticDesignBasedSize(80, 40)};
                  `)}
                `}
              >
                <SearchByBudget {...propsWithApplyHandler} />
              </div>
              <div
                css={css`
                  margin-bottom: 80px;

                  ${tablet(({ setAutomaticDesignBasedSize }) => css`
                    margin-bottom: ${setAutomaticDesignBasedSize(80, 40)};
                  `)}

                  ${mobile(({ setAutomaticDesignBasedSize }) => css`
                    margin-bottom: ${setAutomaticDesignBasedSize(80, 40)};
                  `)}
                `}
              >
                <SearchByDeterioration {...propsWithApplyHandler} />
              </div>
              <div
                css={css`
                  margin-bottom: 80px;

                  ${tablet(({ setAutomaticDesignBasedSize }) => css`
                    margin-bottom: ${setAutomaticDesignBasedSize(80, 40)};
                    padding: 45px 0 50px;
                    background-color: #f6f6f6;
                  `)}

                  ${mobile(({ setAutomaticDesignBasedSize }) => css`
                    margin-bottom: ${setAutomaticDesignBasedSize(80, 40)};
                  `)}
                `}
              >
                <div
                  css={css`
                    display: flex;
                    justify-content: space-between;
                    gap: 0 1em;

                    ${tablet(() => css`
                      flex-direction: column;
                      gap: 3em 0;
                    `)}
                  `}
                >
                  <SearchByCompanyInfomation {...propsWithApplyHandler} />
                  <SearchByBenefit {...propsWithApplyHandler} />
                </div>
              </div>
              <div>
                <div
                  css={css`
                    margin-bottom: 24px;

                    ${tablet(() => css`
                      max-width: 568px;
                      margin-inline: auto;
                      padding: 0 1em;
                    `)}

                    ${mobile(({ setAutomaticDesignBasedSize }) => css`
                      margin-bottom: ${setAutomaticDesignBasedSize(24, 20)};
                    `)}
                  `}
                >
                  <p
                    css={css`
                      color: #000;
                      font-size: 20px;
                      font-weight: bold;

                      ${mobile(({ setAutomaticDesignBasedSize }) => css`
                        font-size: ${setAutomaticDesignBasedSize(20, 18)};
                      `)}
                    `}
                  >
                    工事カテゴリから探す
                  </p>
                </div>
                <div
                  css={css`
                    display: flex;
                    justify-content: space-between;
                    gap: 0 1em;

                    ${tablet(() => css`
                      flex-direction: column;
                      gap: 3em 0;
                    `)}
                  `}
                >
                  <SearchByWallAndRoofConstructionType {...propsWithApplyHandler} />
                  <SearchByWallConstructionType {...propsWithApplyHandler} />
                </div>
              </div>
            </div>
            <PageTopButton {...props} />
          </div>
        </div>
        <Footer {...props} />
      </div>
    </div>
  );
};
